import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useChatStore } from '../../store/chatStore'
import { useThemeStore } from '../../store/themeStore'
import { 
  MagnifyingGlassIcon, 
  PlusIcon, 
  Cog6ToothIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import ChatList from './ChatList'
import LoadingSpinner from '../LoadingSpinner'

const Sidebar = ({ onToggle, onShowProfile, onShowSearch, onShowCreateChat }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isCollapsed, setIsCollapsed] = useState(false)
  
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { chats, isLoading, fetchChats } = useChatStore()
  const { theme, toggleTheme } = useThemeStore()

  useEffect(() => {
    if (chats.length === 0) {
      fetchChats()
    }
  }, [fetchChats, chats.length])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const filteredChats = chats.filter(chat => {
    if (!searchQuery.trim()) return true
    
    const query = searchQuery.toLowerCase()
    
    // Search by chat name (for group chats)
    if (chat.isGroupChat && chat.name) {
      return chat.name.toLowerCase().includes(query)
    }
    
    // Search by participant names (for direct chats)
    return chat.participants.some(participant => 
      participant.username?.toLowerCase().includes(query) ||
      participant.email?.toLowerCase().includes(query)
    )
  })

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          {/* User Avatar */}
          <button
            onClick={onShowProfile}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
          >
            {user?.avatar ? (
              <img 
                src={user.avatar} 
                alt={user.username}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <span className="text-primary-600 dark:text-primary-400 font-medium">
                {user?.username?.charAt(0).toUpperCase()}
              </span>
            )}
          </button>
          
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                Chats
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {user?.username}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            {theme === 'light' ? (
              <MoonIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <SunIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {/* Collapse Toggle */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors lg:hidden"
          >
            {isCollapsed ? (
              <Bars3Icon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <>
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-4 space-y-2">
            <button
              onClick={onShowCreateChat}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              <span>New Chat</span>
            </button>
            
            <button
              onClick={onShowSearch}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
              <span>Search Messages</span>
            </button>
          </div>

          {/* Chat List */}
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {isLoading ? (
              <div className="p-4">
                <LoadingSpinner size="md" />
              </div>
            ) : filteredChats.length > 0 ? (
              <ChatList chats={filteredChats} />
            ) : (
              <div className="p-4 text-center">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {searchQuery ? 'No chats found' : 'No chats yet'}
                </p>
                {!searchQuery && (
                  <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                    Create a new chat to get started
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <button
                onClick={onShowProfile}
                className="flex items-center space-x-2 flex-1 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
              >
                <Cog6ToothIcon className="h-4 w-4" />
                <span className="text-sm">Settings</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors"
                title="Logout"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Sidebar